import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPreview,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatRadioModule } from '@angular/material/radio';
import {
  MatSlideToggleChange,
  MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';
import {
  findAllSimilarServiceTypesForCalendarLegend,
  Fitter,
  FitterServiceType,
  getGlassNameDefaultKey,
  getOperationWithGlassKey,
  Glass,
  LoggerLevelType,
  OperationType,
} from '@sc/data-access-models';
import { JsonListKey, StorageService } from '@sc/data-access-storage';
import { TranslatorService } from '@sc/data-access-translation';
import { ResourcesManagerService } from '@sc/feature-fitter-shared-libs';
import { SnackbarService } from '@sc/feature-snackbar';
import {
  CheckboxComponent,
  ForceRadioNameDirective,
  IconComponent,
  InputTextComponent,
  Task,
} from '@sc/ui-components';
import { take } from 'rxjs/operators';
import { CalendarViewParams } from '../../models';
import {
  PlanningCalendarService,
  PlanningSandboxService,
} from '../../services';
import { ICONS } from '../../const';

type FitterServiceTypeFilter = FitterServiceType & {
  fitters: { id: string; city: string; display: boolean }[];
  isDisplayCalibration: boolean;
};

@Component({
  selector: 'sc-planning-filters',
  styleUrls: ['./planning-filters.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    InputTextComponent,
    MatSlideToggleModule,
    MatRadioModule,
    CdkDropList,
    NgFor,
    CdkDrag,
    CdkDragPreview,
    IconComponent,
    CdkDragHandle,
    ForceRadioNameDirective,
    TranslateModule,
    CheckboxComponent,
    AsyncPipe,
  ],
  template: `
    <div>
      <div class="filters-container">
        <h1>{{ 'PlanningFilters.title' | translate }}</h1>

        <sc-input-text
          *ngIf="displayFitterSelector() | async"
          #adminFitterInput
          class="admin-fitter-input"
          [label]="'PlanningFilters.searchFitterLabel' | translate"
          [suggestions]="adminSuggestedFitters"
          suggestionsLabelName="name"
          (updateSuggestions)="onAdminFitterInputSuggestionUpdate($event)"
          (selectSuggestion)="onAdminFitterInputSuggestionSelect($event)"
        ></sc-input-text>
        <div *ngIf="filters?.length > 0">
          <div class="center-view">
            {{ 'PlanningFilters.centerViewTitle' | translate }}
          </div>
          <div class="calendar-view-params-container">
            <h5>
              {{ 'PlanningFilters.calendarViewParams.tech.label' | translate }}
            </h5>
            <div class="view-toggle-row">
              <span
                [class.selected]="!calendarView.detailedTechCenter"
                aria-hidden="true"
              >
                {{
                  'PlanningFilters.calendarViewParams.tech.condensed'
                    | translate
                }}
              </span>
              <mat-slide-toggle
                [disableRipple]="true"
                [hideIcon]="true"
                [checked]="calendarView.detailedTechCenter"
                [aria-label]="
                  'PlanningFilters.calendarViewParams.tech.ariaLabel'
                    | translate
                      : {
                          selectedOption:
                            ((calendarView.detailedTechCenter
                              ? 'PlanningFilters.calendarViewParams.tech.detailed'
                              : 'PlanningFilters.calendarViewParams.tech.condensed'
                            ) | translate)
                        }
                "
                (change)="onDetailTechChange($event)"
              ></mat-slide-toggle>
              <span
                [class.selected]="calendarView.detailedTechCenter"
                aria-hidden="true"
                >{{
                  'PlanningFilters.calendarViewParams.tech.detailed' | translate
                }}</span
              >
            </div>
            <h5>
              {{
                'PlanningFilters.calendarViewParams.interventionVehicle.label'
                  | translate
              }}
            </h5>
            <div class="view-toggle-row">
              <span
                [class.selected]="!calendarView.detailedInterventionVehicle"
                aria-hidden="true"
              >
                {{
                  'PlanningFilters.calendarViewParams.interventionVehicle.condensed'
                    | translate
                }}
              </span>
              <mat-slide-toggle
                [disableRipple]="true"
                [hideIcon]="true"
                [checked]="calendarView.detailedInterventionVehicle"
                [aria-label]="
                  'PlanningFilters.calendarViewParams.interventionVehicle.ariaLabel'
                    | translate
                      : {
                          selectedOption:
                            ((calendarView.detailedInterventionVehicle
                              ? 'PlanningFilters.calendarViewParams.interventionVehicle.detailed'
                              : 'PlanningFilters.calendarViewParams.interventionVehicle.condensed'
                            ) | translate)
                        }
                "
                (change)="onDetailInterventionVehicleChange($event)"
              ></mat-slide-toggle>
              <span
                [class.selected]="calendarView.detailedInterventionVehicle"
                aria-hidden="true"
                >{{
                  'PlanningFilters.calendarViewParams.interventionVehicle.detailed'
                    | translate
                }}</span
              >
            </div>
            <h5>
              {{
                'PlanningFilters.calendarViewParams.replacementVehicle.label'
                  | translate
              }}
            </h5>
            <div class="view-toggle-row">
              <span
                [class.selected]="!calendarView.detailedRV"
                aria-hidden="true"
              >
                {{
                  'PlanningFilters.calendarViewParams.replacementVehicle.condensed'
                    | translate
                }}
              </span>
              <mat-slide-toggle
                [disableRipple]="true"
                [hideIcon]="true"
                [checked]="calendarView.detailedRV"
                [aria-label]="
                  'PlanningFilters.calendarViewParams.replacementVehicle.ariaLabel'
                    | translate
                      : {
                          selectedOption:
                            ((calendarView.detailedRV
                              ? 'PlanningFilters.calendarViewParams.replacementVehicle.detailed'
                              : 'PlanningFilters.calendarViewParams.replacementVehicle.condensed'
                            ) | translate)
                        }
                "
                (change)="onDetailRVChange($event)"
              ></mat-slide-toggle>
              <span
                [class.selected]="calendarView.detailedRV"
                aria-hidden="true"
                >{{
                  'PlanningFilters.calendarViewParams.replacementVehicle.detailed'
                    | translate
                }}</span
              >
            </div>
            <h5>
              {{
                'PlanningFilters.calendarViewParams.center.label' | translate
              }}
            </h5>
            <div class="view-toggle-row">
              <span
                [class.selected]="calendarView.monoCenter"
                aria-hidden="true"
                >{{
                  'PlanningFilters.calendarViewParams.center.mono' | translate
                }}</span
              >
              <mat-slide-toggle
                [disableRipple]="true"
                [hideIcon]="true"
                [checked]="!calendarView.monoCenter"
                [aria-label]="
                  'PlanningFilters.calendarViewParams.center.ariaLabel'
                    | translate
                      : {
                          selectedOption:
                            ((calendarView.monoCenter
                              ? 'PlanningFilters.calendarViewParams.center.mono'
                              : 'PlanningFilters.calendarViewParams.center.multi'
                            ) | translate)
                        }
                "
                (change)="onMonoCenterChange($event)"
              ></mat-slide-toggle>
              <span
                [class.selected]="!calendarView.monoCenter"
                aria-hidden="true"
              >
                {{
                  'PlanningFilters.calendarViewParams.center.multi' | translate
                }}
              </span>
            </div>
          </div>
        </div>
        <mat-radio-group
          cdkDropList
          [cdkDropListDisabled]="filters?.length <= 1"
          [cdkDropListData]="filters"
          (change)="setRadio($event)"
          (cdkDropListDropped)="dropCenter($event)"
        >
          <div
            *ngFor="let task of filters"
            cdkDrag
            [cdkDragData]="task"
            cdkDragPreviewClass="center-drag-preview"
            cdkDragLockAxis="y"
          >
            <div *cdkDragPreview>
              <sc-icon name="drag_drop"></sc-icon>
              <h3>
                {{ task.name }}
              </h3>
            </div>

            <div class="name-drag-handle-container">
              <sc-icon
                *ngIf="filters?.length > 1"
                name="drag_drop"
                cdkDragHandle
              ></sc-icon>

              <sc-checkbox
                *ngIf="!calendarView.monoCenter"
                class="center-title"
                [disabled]="disabled || task.disabled"
                [value]="task.checked"
                (changeValue)="setAll(task, $event)"
              >
                <span class="sc-design-text-2">{{ task.name }}</span>
              </sc-checkbox>

              <mat-radio-button
                *ngIf="calendarView.monoCenter"
                class="center-title"
                [value]="task"
                [forceRadioName]="task.name"
                [checked]="task?.checked"
              >
                <span class="sc-design-text-2">{{ task.name }}</span>
              </mat-radio-button>
            </div>

            <ul>
              <div
                *ngIf="!calendarView.monoCenter || task.checked"
                cdkDropList
                [cdkDropListDisabled]="task.subtasks.length <= 1"
                [cdkDropListData]="task"
                (cdkDropListDropped)="dropSubtask($event)"
              >
                <li
                  *ngFor="let subtask of task.subtasks"
                  cdkDrag
                  [cdkDragData]="subtask"
                  cdkDragPreviewClass="subtask-drag-preview"
                  cdkDragLockAxis="y"
                >
                  <div *cdkDragPreview>
                    <sc-icon name="drag_drop"></sc-icon>
                    <h3>
                      {{ subtask.name }}
                    </h3>
                  </div>

                  <div class="subtask_menu">
                    <div class="flex-center">
                      <sc-icon
                        *ngIf="task.subtasks.length > 1"
                        name="drag_drop"
                        cdkDragHandle
                      ></sc-icon>

                      <sc-checkbox
                        [disabled]="disabled || subtask.disabled"
                        [value]="task.checked && subtask.checked"
                        (changeValue)="updateAllComplete(task, subtask, $event)"
                      >
                        <span class="sc-design-text-3">{{
                          subtask.name?.split('##').length > 1
                            ? subtask.name?.split('##')[1]
                            : subtask.name
                        }}</span>
                      </sc-checkbox>
                    </div>

                    <sc-icon
                      *ngIf="subtask?.subtasks?.length"
                      class="menu_arrow"
                      [name]="
                        subtask?.subtaskopened ? 'arrow_bottom' : 'arrow_top'
                      "
                      (click)="subtask.subtaskopened = !subtask?.subtaskopened"
                    ></sc-icon>
                  </div>
                  <mat-radio-group
                    *ngIf="subtask?.subtasks?.length && subtask.subtaskopened"
                    class="subtasks-radio-group"
                    (change)="
                      applyTechniciansOrInterventionVehiclesOnPlanning($event)
                    "
                  >
                    <mat-radio-button
                      class="subtasks-radio-button"
                      *ngFor="let technician of subtask?.subtasks"
                      [value]="technician"
                      [forceRadioName]="technician?.name"
                      [checked]="technician?.checked"
                    >
                      <span class="sc-design-text-3">{{
                        technician?.name
                      }}</span>
                    </mat-radio-button>
                  </mat-radio-group>
                </li>
              </div>
            </ul>
          </div>
        </mat-radio-group>
      </div>

      <div class="service-types-legend-container">
        <h3>{{ 'PlanningFilters.legend' | translate }}</h3>

        <div
          class="subtask_menu"
          (click)="showAvailableServices = !showAvailableServices"
        >
          {{ 'PlanningFilters.filters.title' | translate }}
          <sc-icon
            class="menu_arrow"
            [name]="showAvailableServices ? 'arrow_bottom' : 'arrow_top'"
          ></sc-icon>
        </div>
        <div *ngIf="showAvailableServices">
          <ng-container *ngFor="let availableService of availableServices">
            <div
              class="available-service-type"
              *ngIf="shouldDisplayService(availableService)"
            >
              <div class="service-type">
                <div
                  class="service-type-color"
                  [style.background-color]="availableService?.color"
                ></div>
                <div class="service-type-name">
                  <div>{{ getServiceTypeDisplayName(availableService) }}</div>
                  <div
                    *ngIf="getServiceFittersList(availableService).length > 0"
                    class="service-type-locations"
                  >
                    ({{ getServiceFittersList(availableService).join(', ') }})
                  </div>
                </div>
              </div>
            </div>
          </ng-container>
        </div>

        <hr class="mb-m" />

        <div class="subtask_menu" (click)="showIconList = !showIconList">
          {{ 'PlanningFilters.icons.title' | translate }}
          <sc-icon
            class="menu_arrow"
            [name]="showIconList ? 'arrow_bottom' : 'arrow_top'"
          ></sc-icon>
        </div>
        <div *ngIf="showIconList">
          <ng-container *ngFor="let entries of Object.entries(iconList)">
            <div class="available-service-type">
              <div class="service-type">
                <span
                  class="planning-icon {{ entries[1].classname }}"
                  style="background-image: url('/assets/default/icons.new/{{
                    entries[1].name
                  }}.svg');"
                ></span>
                <div class="service-type-name">
                  <div>
                    {{ 'PlanningFilters.icons.' + entries[0] | translate }}
                  </div>
                </div>
              </div>
            </div>
          </ng-container>
        </div>
      </div>
    </div>
  `,
})
export class PlanningFiltersComponent {
  @ViewChild('adminFitterInput') adminFitterInput?: InputTextComponent;

  @Input() set fitters(fitters: Fitter[]) {
    this._fitters = fitters;
    this.filters = this.planningSB.generateFiltersFromFitters(
      fitters,
      this.filters
    );

    if (this.filters?.length > 0) {
      this.planningSB.storeResourcesFilters(this.filters);
    }

    this.applyResourcesOnPlanning(this.filters);

    this.availableServices = this.generateAvailableServices(fitters);
    this.changeRef.markForCheck();
  }

  _fitters: Fitter[];

  calendarView: CalendarViewParams = this.storage.getItem(
    'calendar-filter-vue',
    localStorage,
    {
      detailedTechCenter: false,
      detailedInterventionVehicle: false,
      detailedRV: false,
      monoCenter: true,
    }
  );

  activeCentersMulti: string[];

  filters: Task[];
  availableServices: FitterServiceTypeFilter[] = [];

  adminSuggestedFitters: { id: string; name: string }[] = [];
  currentAdminFitterInputValue = '';
  iconList = ICONS;
  showAvailableServices = false;
  showIconList = false;

  disabled = false;

  constructor(
    private readonly translator: TranslatorService,
    private readonly planningSB: PlanningSandboxService,
    private readonly planningCalendarService: PlanningCalendarService,
    private readonly resManagerService: ResourcesManagerService,
    private readonly changeRef: ChangeDetectorRef,
    private readonly storage: StorageService,
    private readonly snackbarService: SnackbarService
  ) {}

  private generateAvailableServices(fitters: Fitter[]) {
    if (!fitters) return [];

    const availableServices: FitterServiceTypeFilter[] = [];
    if (fitters.length) {
      fitters.forEach((fitter) => {
        fitter.serviceTypes?.forEach((serviceType) => {
          const allSimilar = findAllSimilarServiceTypesForCalendarLegend(
            serviceType,
            availableServices
          ) as FitterServiceTypeFilter[];

          const foundSameColor = allSimilar.find(
            (similar) =>
              similar.color?.toLowerCase() === serviceType.color?.toLowerCase()
          );

          const displayed =
            this.filters?.findIndex(
              (filter) =>
                filter.id === fitter.id && this.hasMoreThanOneSubCheck(filter)
            ) > -1;

          if (!foundSameColor) {
            availableServices.push({
              ...serviceType,
              name:
                serviceType.glassType !== 'other' && serviceType?.default
                  ? serviceType.glassType === 'windshield'
                    ? this.getGlassNameWithOperation(
                        serviceType.glassType,
                        serviceType.operationType
                      )
                    : this.getGlassName(serviceType.glassType)
                  : serviceType.name,
              fitters: [
                { id: fitter.id, city: fitter.city, display: displayed },
              ],
              isDisplayCalibration: serviceType.calibration ?? false,
            });
          } else {
            foundSameColor.fitters.push({
              id: fitter.id,
              city: fitter.city,
              display: displayed,
            });
          }
        });
      });
    }

    availableServices.sort((serviceA, serviceB) =>
      serviceA.name.localeCompare(serviceB.name)
    );

    return availableServices;
  }

  displayFitterSelector() {
    return this.planningSB.userIsSuperAdmin();
  }

  onDetailTechChange(detailedTechChange: MatSlideToggleChange) {
    this.calendarView.detailedTechCenter = detailedTechChange.checked;
    this.onChangeCenterView();
    this.onChangeFilters(this.filters, true);
  }

  onDetailInterventionVehicleChange(
    detailedInterventionVehicle: MatSlideToggleChange
  ) {
    this.calendarView.detailedInterventionVehicle =
      detailedInterventionVehicle.checked;
    this.onChangeCenterView();
    this.onChangeFilters(this.filters, true);
  }

  onDetailRVChange(detailedRVChange: MatSlideToggleChange) {
    this.calendarView.detailedRV = detailedRVChange.checked;
    this.onChangeCenterView();
    this.onChangeFilters(this.filters, true);
  }

  onMonoCenterChange(monoCenterChange: MatSlideToggleChange) {
    this.calendarView.monoCenter = !monoCenterChange.checked;

    if (this.calendarView.monoCenter) {
      const activeCentersMultiTask = this.filters.filter(
        (task) => task.checked
      );
      this.storage.setItem(
        'agenda-active-resources-in-multi',
        activeCentersMultiTask.map((task) => task.id)
      );
      this.filters?.forEach((task) =>
        this.setAllWithoutSave(task, task === activeCentersMultiTask?.[0])
      );
    } else {
      const agendaActiveResourcesInMulti = this.storage.getItem<
        JsonListKey,
        string
      >('agenda-active-resources-in-multi');
      this.filters?.forEach((task) =>
        this.setAllWithoutSave(
          task,
          task.checked ||
            agendaActiveResourcesInMulti?.findIndex(
              (value) => task.id === value
            ) !== -1
        )
      );
    }

    this.onChangeCenterView();
    this.onChangeFilters(this.filters, true);
  }

  getGlassNameWithOperation(
    glass: Glass,
    operationType: OperationType
  ): string {
    return this.translator.getInstantTranslation(
      getOperationWithGlassKey(operationType),
      { glassName: this.getGlassName(glass) }
    );
  }

  getGlassName(glass: Glass): string {
    return this.translator.getInstantTranslation(getGlassNameDefaultKey(glass));
  }

  async applyResourcesOnPlanning(tasks: Task[]) {
    const resourcesToDisplay: string[] = [];
    await new Promise((resolve) => setTimeout(resolve, 200)); // For responsiveness of checkbox
    tasks.forEach((task) => {
      this.availableServices.forEach((availableService) => {
        availableService.fitters.forEach((serviceFitterInfos) => {
          if (serviceFitterInfos.id === task.id) {
            serviceFitterInfos.display = this.hasMoreThanOneSubCheck(task);
          }
        });
      });

      task.subtasks.forEach((subtask) => {
        if (task.checked && subtask.checked) {
          resourcesToDisplay.push(subtask.id as string);
        }
      });
    });

    this.planningSB.updateResourcesToDisplay(resourcesToDisplay);
  }

  applyTechniciansOrInterventionVehiclesOnPlanning(event) {
    if ((event?.value?.id as string)?.split('_')[2] === 'technician') {
      this.applyTechniciansOnPlanning(event);
    } else if (
      (event?.value?.id as string)?.split('_')[2] === 'interventionVehicle'
    ) {
      this.applyInterventionVehicleOnPlanning(event);
    }
  }

  applyTechniciansOnPlanning(event) {
    const fitterId = (event?.value?.id as string)?.split('_')[0];
    const name = event?.value?.name;
    const isTechView = this.planningCalendarService.filtersAreTechnicianView(
      this.filters
    );
    const tasks = this.filters?.map((filter) => {
      if (filter.id !== fitterId) {
        return filter;
      } else
        return {
          ...filter,
          subtasks: filter?.subtasks?.map((subtask) => {
            if (
              this.resManagerService.getResourceType(subtask.id) ===
                'IN_CENTER' &&
              !isTechView
            ) {
              return subtask?.subtasks.length
                ? {
                    ...subtask,
                    subtasks: subtask?.subtasks?.map((technician) => {
                      return {
                        ...technician,
                        checked: technician?.name === name,
                      };
                    }),
                  }
                : subtask;
            } else return subtask;
          }),
        };
    });
    this.onChangeFilters(tasks);
  }

  applyInterventionVehicleOnPlanning(event) {
    const fitterId = (event?.value?.id as string)?.split('_')[0];
    const name = event?.value?.name;
    const isInterventionVehicleView =
      this.planningCalendarService.filtersAreInterventionVehicleView(
        this.filters
      );
    const tasks = this.filters?.map((filter) => {
      if (filter.id !== fitterId) {
        return filter;
      } else
        return {
          ...filter,
          subtasks: filter?.subtasks?.map((subtask) => {
            if (
              this.resManagerService.getResourceType(subtask.id) ===
                'OUTSIDE' &&
              !isInterventionVehicleView
            ) {
              return subtask?.subtasks.length
                ? {
                    ...subtask,
                    subtasks: subtask?.subtasks?.map((interventionVehicle) => {
                      return {
                        ...interventionVehicle,
                        checked: interventionVehicle?.name === name,
                      };
                    }),
                  }
                : subtask;
            } else return subtask;
          }),
        };
    });
    this.onChangeFilters(tasks);
  }

  hasMoreThanOneSubCheck(task: Task) {
    return task
      ? task?.subtasks?.reduce((acc, current) => acc || current?.checked, false)
      : false;
  }

  shouldDisplayService(service: FitterServiceTypeFilter) {
    return (
      service.fitters.filter((fitterInfos) => fitterInfos.display).length > 0
    );
  }

  getServiceFittersList(service: FitterServiceTypeFilter) {
    const containsAllDisplayed = this.filters
      ?.filter((f) => this.hasMoreThanOneSubCheck(f))
      .reduce(
        (acc, current) =>
          acc &&
          service.fitters.findIndex(
            (fitterInfos) => fitterInfos.id === current.id
          ) > -1,
        true
      );

    return service.fitters
      .filter((fitterInfos) => fitterInfos.display && !containsAllDisplayed)
      .map((fitterInfos) => fitterInfos.city);
  }

  getServiceTypeDisplayName(service: FitterServiceTypeFilter) {
    return `${service.name} ${
      service.isDisplayCalibration
        ? this.translator.getInstantTranslation(
            'PlanningFilters.withCalibration'
          )
        : ''
    }`;
  }

  dropCenter(dropEvent: CdkDragDrop<Task[], Task[], Task>) {
    if (dropEvent.previousIndex !== dropEvent.currentIndex) {
      this.filters = dropEvent.container.data;

      moveItemInArray(
        this.filters,
        dropEvent.previousIndex,
        dropEvent.currentIndex
      );

      this.onChangeFilters(this.filters, true);
    }
  }

  dropSubtask(dropEvent: CdkDragDrop<Task, Task, Task>) {
    if (dropEvent.previousIndex !== dropEvent.currentIndex) {
      moveItemInArray(
        dropEvent.container.data.subtasks,
        dropEvent.previousIndex,
        dropEvent.currentIndex
      );

      this.onChangeFilters(this.filters, true);
    }
  }

  onChangeCenterView() {
    this.storage.setItem(
      'calendar-filter-vue',
      this.calendarView,
      localStorage,
      {
        detailedTechCenter: false,
        detailedInterventionVehicle: false,
        detailedRV: false,
        monoCenter: true,
      }
    );

    this.filters = this.planningSB.generateFiltersFromFitters(
      this._fitters,
      this.filters,
      this.calendarView.detailedTechCenter,
      this.calendarView.detailedInterventionVehicle,
      this.calendarView.detailedRV
    );
  }

  onChangeFilters(tasks: Task[], forceUpdatePlanning = false) {
    this.planningSB
      .userIsSuperAdmin()
      .pipe(take(1))
      .subscribe((isSuperAdmin) => {
        const adminUncheckedCenter =
          !this.calendarView.monoCenter &&
          isSuperAdmin &&
          tasks.findIndex((task) => !task.checked) > -1;

        let filteredTasks = tasks;
        if (adminUncheckedCenter) {
          filteredTasks = tasks.filter((task) => task.checked);
        }

        this.filters = filteredTasks;
        this.planningSB.storeResourcesFilters(filteredTasks);

        if (adminUncheckedCenter || forceUpdatePlanning) {
          this.planningSB.updatePlanning(
            false,
            true,
            filteredTasks.map((task) => task.id.toString())
          );
        }

        this.applyResourcesOnPlanning(filteredTasks);
      });
  }

  onAdminFitterInputSuggestionUpdate(suggestionUpdateEvent: string) {
    this.currentAdminFitterInputValue = suggestionUpdateEvent?.trim();
    if (this.currentAdminFitterInputValue?.length) {
      this.planningSB
        .getFittersSuggestions(this.currentAdminFitterInputValue)
        .pipe(take(1))
        .subscribe(
          (result) => {
            this.adminSuggestedFitters = result?.filter(
              (suggestedFitter) =>
                this._fitters?.findIndex(
                  (fitter) => fitter?.id === suggestedFitter?.id
                ) === -1
            );
            this.changeRef.markForCheck();
          },
          (error: HttpErrorResponse) => {
            this.snackbarService.showError(
              this.translator.getInstantTranslation('Snackbar.errorTitle'),
              this.translator.getInstantTranslation(
                error?.error?.message ||
                  'PlanningFilters.fittersSuggestionError'
              ),
              5,
              LoggerLevelType.WARNING
            );

            console.error(error);
          }
        );
    }
  }

  onAdminFitterInputSuggestionSelect(suggestionSelectEvent: {
    id: string;
    name: string;
  }) {
    if (this.adminFitterInput) {
      this.adminFitterInput.writeValue(this.currentAdminFitterInputValue);
    }

    // Check maximum number of fitters shown together here
    if (
      this.filters?.findIndex((filter) => filter.id) > -1 ||
      this.filters?.length === 0
    ) {
      const fitterIds = [
        ...(this.filters?.map((f) => f.id.toString()) || []),
        suggestionSelectEvent.id,
      ];
      this.storage.setItem('agenda-active-resources-in-multi', fitterIds);
      this.planningSB.updatePlanning(false, true, fitterIds);
    }
  }

  setAll(task: Task, checked: boolean) {
    this.setAllWithoutSave(task, checked);

    this.onChangeFilters(this.filters);
  }

  setAllWithoutSave(task: Task, checked: boolean) {
    if (this.disabled) return;

    task.checked = checked;

    if (task.subtasks?.length) {
      task.subtasks.forEach((t) => {
        t.checked = checked;
      });
    }
  }

  setRadio(event) {
    const id = event?.value?.id;
    const tasks = this.filters?.map((task) => {
      return task?.id == id
        ? {
            ...task,
            checked: true,
            subtasks: task.subtasks.map((subtask) => {
              return { ...subtask, checked: true };
            }),
          }
        : {
            ...task,
            checked: false,
            subtasks: task.subtasks.map((subtask) => {
              return { ...subtask, checked: false };
            }),
          };
    });
    this.filters = tasks;
    this.onChangeFilters(tasks);
  }

  updateAllComplete(parentTask: Task, task: Task, checked: boolean) {
    if (this.disabled) return;

    task.checked = checked;

    parentTask.checked =
      !!parentTask.subtasks?.filter((t) => t.checked)?.length || false;

    this.onChangeFilters(this.filters);
  }

  protected readonly Object = Object;
}
